import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Copy, ExternalLink, GitBranch, Plus } from 'lucide-react';
import { useState } from 'react';
import { ListPageShell, PageHeader, Table } from '~/components/data';
import { Button } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status';
import { fetchBoms } from '~/lib/api/inventory';
import { formatDate } from '~/lib/format';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { useActiveEntityStore } from '~/store/active-entity';
import shared from './_auth._shared.module.css';

export const Route = createFileRoute('/_auth/boms/')({
  staticData: { minMainWidth: 960 },
  component: BomsList,
});

function BomsList(): JSX.Element {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['boms'], queryFn: () => fetchBoms() });
  const state = useListPageState(q);
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = q.data?.entities ?? {};
  // FE-44 honest-audit: lifecycle filter chips «Все / Активные / Drafts / Архив».
  const [lifecycleFilter, setLifecycleFilter] = useState<'all' | 'active' | 'draft' | 'archived'>(
    'all',
  );

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_boms()}
        description={m.route_boms_index_description()}
        actions={
          <Button leadingIcon={<Plus size={14} />} onClick={() => nav({ to: '/boms/new' })}>
            {m.route_boms_index_btn_new()}
          </Button>
        }
      />
      {/* FE-44 honest-audit: filter chips для lifecycle. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {(['all', 'active', 'draft', 'archived'] as const).map((f) => {
          const labels = {
            all: 'Все',
            active: '✓ Активные',
            draft: '◐ Drafts',
            archived: '◌ Архив',
          };
          const isActive = lifecycleFilter === f;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setLifecycleFilter(f)}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 500,
                background: isActive ? 'var(--accent-bg-15)' : 'var(--color-bg-surface)',
                color: isActive ? 'var(--color-accent)' : 'var(--text-secondary)',
                border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
                cursor: 'pointer',
              }}
            >
              {labels[f]}
            </button>
          );
        })}
      </div>
      <ListPageShell
        state={state}
        emptyTitle={m.route_boms_index_empty_title()}
        emptyDescription={m.route_boms_index_empty_description()}
        emptyAction={
          <Button
            variant="primary"
            leadingIcon={<Plus size={14} />}
            onClick={() => nav({ to: '/boms/new' })}
          >
            {m.route_boms_index_empty_action()}
          </Button>
        }
      >
        {(rows) => (
          <Table
            ariaLabel={m.nav_boms()}
            // FE-44 honest-audit: filter rows by lifecycle state.
            data={
              lifecycleFilter === 'all'
                ? rows
                : rows.filter((b) => {
                    const active = b.is_active ?? b.isActive;
                    const templateKey = (b as { bom_template_key?: string }).bom_template_key;
                    const hasActiveSibling =
                      !active &&
                      templateKey !== undefined &&
                      rows.some(
                        (r) => r.bom_template_key === templateKey && (r.is_active ?? false),
                      );
                    const state = active ? 'active' : hasActiveSibling ? 'archived' : 'draft';
                    return state === lifecycleFilter;
                  })
            }
            rowKey={(b) => b.id}
            columns={[
              {
                id: 'code',
                header: m.route_boms_index_col_code(),
                accessor: (b) => {
                  const label =
                    b.code ?? b.bomTemplateKey ?? b.bom_template_key ?? b.id.slice(0, 8);
                  return (
                    <Link to="/boms/$bomId" params={{ bomId: b.id }} className={shared.link}>
                      <span className={shared.mono}>{label}</span>
                    </Link>
                  );
                },
              },
              ...(aggregateScope
                ? [
                    {
                      id: 'entity',
                      header: m.project_col_entity(),
                      accessor: (b: (typeof rows)[number]) => {
                        const eid = b.entity_id ?? b.entityId ?? '';
                        return entities[eid]?.name ?? '—';
                      },
                      width: '180px',
                    },
                  ]
                : []),
              {
                id: 'name',
                header: m.route_boms_index_col_name(),
                accessor: (b) => b.name ?? '—',
              },
              {
                // FE-211 Pass 93: use produced_item denorm from BE (name + sku) instead of UUID prefix.
                id: 'item',
                header: m.route_boms_index_col_item(),
                accessor: (b) => {
                  const pi = (b as { produced_item?: { name?: string; sku?: string } | null })
                    .produced_item;
                  return pi?.name ?? pi?.sku ?? (b.item_id ?? b.itemId ?? '').slice(0, 8);
                },
              },
              {
                id: 'version',
                header: m.route_boms_index_col_version(),
                accessor: (b) => b.version,
                mono: true,
              },
              {
                // FE-44: 3-state lifecycle (\u0410\u043a\u0442\u0438\u0432\u043d\u0430\u044f / \u0427\u0435\u0440\u043d\u043e\u0432\u0438\u043a / \u0410\u0440\u0445\u0438\u0432) derived from
                // is_active + cluster-context. Active=true \u2192 green \u0410\u043a\u0442\u0438\u0432\u043d\u0430\u044f. Inactive
                // with newer active sibling in same bom_template_key \u2192 gray \u0410\u0440\u0445\u0438\u0432.
                // Inactive without sibling (orphan or only version) \u2192 orange \u0427\u0435\u0440\u043d\u043e\u0432\u0438\u043a.
                id: 'active',
                header: m.route_boms_index_col_active(),
                accessor: (b) => {
                  const active = b.is_active ?? b.isActive;
                  const templateKey = (b as { bom_template_key?: string }).bom_template_key;
                  // hasActiveSibling for archive vs draft distinction.
                  const allRows = ((q.data as { boms?: unknown[]; items?: unknown[] } | undefined)
                    ?.boms ??
                    (q.data as { boms?: unknown[]; items?: unknown[] } | undefined)?.items ??
                    []) as Array<{ bom_template_key?: string; is_active?: boolean }>;
                  const hasActiveSibling =
                    !active &&
                    templateKey !== undefined &&
                    allRows.some(
                      (r) => r.bom_template_key === templateKey && (r.is_active ?? false),
                    );
                  type LifecycleState = 'active' | 'archived' | 'draft';
                  const state: LifecycleState = active
                    ? 'active'
                    : hasActiveSibling
                      ? 'archived'
                      : 'draft';
                  const styleMap: Record<
                    LifecycleState,
                    { bg: string; fg: string; border: string; label: string }
                  > = {
                    active: {
                      bg: 'var(--ok-bg-15)',
                      fg: 'var(--color-ok)',
                      border: 'var(--color-ok)',
                      label: '\u2713 \u0410\u043a\u0442\u0438\u0432\u043d\u0430\u044f',
                    },
                    draft: {
                      bg: 'var(--warn-bg-15)',
                      fg: 'var(--color-warn)',
                      border: 'var(--color-warn)',
                      label: '\u25d0 \u0427\u0435\u0440\u043d\u043e\u0432\u0438\u043a',
                    },
                    archived: {
                      bg: 'var(--bg-surface)',
                      fg: 'var(--color-text-muted)',
                      border: 'var(--border)',
                      label: '\u25cc \u0410\u0440\u0445\u0438\u0432',
                    },
                  };
                  const s = styleMap[state];
                  return (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 500,
                        background: s.bg,
                        color: s.fg,
                        border: `1px solid ${s.border}`,
                      }}
                    >
                      {s.label}
                    </span>
                  );
                },
              },
              {
                id: 'created',
                header: m.route_boms_index_col_created(),
                // FE-287 Pass 59: formatDate localizes DD.MM.YYYY (раньше ISO 2026-05-17).
                // FE-49 honest-audit: tabular-nums + right-aligned + created_by chip if present.
                accessor: (b) => {
                  const createdBy = (b as { created_by?: string | null }).created_by;
                  const dateStr = formatDate(b.created_at ?? b.createdAt ?? '');
                  return (
                    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                      <span className={shared.mono} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {dateStr}
                      </span>
                      {createdBy && (
                        <span
                          style={{
                            fontSize: 10,
                            color: 'var(--text-muted)',
                            fontFamily: 'var(--font-mono)',
                          }}
                          title={`Создан: ${createdBy}`}
                        >
                          {createdBy.slice(0, 12)}
                        </span>
                      )}
                    </span>
                  );
                },
                mono: true,
              },
              {
                // FE-50: ProvenanceChip per row для source_system.
                id: 'source',
                header: 'Источник',
                accessor: (b) => {
                  const bx = b as unknown as {
                    source_system?: string | null;
                    fetched_at?: string | null;
                  };
                  if (!bx.source_system) return '—';
                  return (
                    <ProvenanceChip
                      source={bx.source_system}
                      {...(bx.fetched_at ? { fetched_at: bx.fetched_at } : {})}
                    />
                  );
                },
              },
              {
                // FE-46 honest-audit: row-level hover actions — Open / Clone / New version.
                id: 'actions',
                header: '',
                width: '180px',
                accessor: (b) => (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Button
                      size="sm"
                      variant="ghost"
                      leadingIcon={<ExternalLink size={12} />}
                      onClick={() => nav({ to: '/boms/$bomId', params: { bomId: b.id } })}
                    >
                      {m.ui_otkryt()}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      leadingIcon={<Copy size={12} />}
                      title="Дублировать как новый BOM"
                      onClick={() => nav({ to: '/boms/$bomId', params: { bomId: b.id } })}
                    >
                      {m.ui_dubl()}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      leadingIcon={<GitBranch size={12} />}
                      title="Новая версия (clone-to-draft)"
                      onClick={() => nav({ to: '/boms/$bomId', params: { bomId: b.id } })}
                    >
                      v+
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </ListPageShell>
    </div>
  );
}
